import React, { useCallback, useEffect, useState } from 'react';
import { Table, Tag, Select, Alert, Modal, Radio, Descriptions, message, Empty } from 'antd';
import { LinkOutlined, DisconnectOutlined, SearchOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import clienteAxios from '../../Utils/clienteAxios';
import Boton from '../Atoms/Boton';

/**
 * Administración de cuentas vinculadas.
 *
 * Una persona que trabaja para dos empresas del grupo tiene dos cuentas. Aquí se
 * asocian para que pueda moverse entre ellas sin volver a entrar.
 *
 * Vincular NUNCA es automático: el detector solo propone pares para ahorrar la
 * búsqueda entre más de cien cuentas, y quien decide es siempre quien administra.
 * Por eso conviven las dos entradas —candidatos sugeridos y selección manual— y
 * ambas terminan en la misma confirmación.
 */
const CuentasVinculadas = () => {
  const [candidatos, setCandidatos] = useState([]);
  const [vinculos, setVinculos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [buscando, setBuscando] = useState(false);

  const [parEnRevision, setParEnRevision] = useState(null);
  const [principalId, setPrincipalId] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const [manualA, setManualA] = useState(null);
  const [manualB, setManualB] = useState(null);

  const cargarVinculos = useCallback(() => {
    setCargando(true);
    Promise.all([clienteAxios.get('/cuentas-vinculadas'), clienteAxios.get('/users')])
      .then(([respVinculos, respUsuarios]) => {
        setVinculos(respVinculos.data.data || []);
        // /users responde [ [...usuarios], status ]: la lista va en la posición 0,
        // igual que la lee Users.jsx.
        setUsuarios(respUsuarios.data[0] || []);
      })
      .catch((error) => {
        message.error(error.response?.data?.message || 'No se pudo cargar la información.');
      })
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    cargarVinculos();
  }, [cargarVinculos]);

  const buscarCandidatos = () => {
    setBuscando(true);
    clienteAxios
      .get('/cuentas-vinculadas/candidatos')
      .then((respuesta) => {
        const encontrados = respuesta.data.data || [];
        setCandidatos(encontrados);
        if (encontrados.length === 0) {
          message.info('No se encontraron cuentas que parezcan de la misma persona.');
        }
      })
      .catch((error) => {
        message.error(error.response?.data?.message || 'No se pudo ejecutar la búsqueda.');
      })
      .finally(() => setBuscando(false));
  };

  /** Abre la confirmación. Vale igual para un candidato o para un par manual. */
  const revisarPar = (cuentaA, cuentaB) => {
    setParEnRevision({ a: cuentaA, b: cuentaB });
    // Se propone como principal la que viene de la intranet: es la que la
    // sincronización reconoce, así que conviene que sea la de referencia.
    setPrincipalId(cuentaA.intranet_id ? cuentaA.user_id : cuentaB.user_id);
  };

  const revisarParManual = () => {
    const a = usuarios.find((u) => u.id === manualA);
    const b = usuarios.find((u) => u.id === manualB);

    if (!a || !b) {
      message.warning('Elige las dos cuentas.');
      return;
    }
    if (a.id === b.id) {
      message.warning('Son la misma cuenta.');
      return;
    }

    revisarPar(
      { user_id: a.id, name: a.name, email: a.email, company: a.company, intranet_id: null },
      { user_id: b.id, name: b.name, email: b.email, company: b.company, intranet_id: null }
    );
  };

  const vincular = () => {
    const { a, b } = parEnRevision;
    const secundariaId = principalId === a.user_id ? b.user_id : a.user_id;

    setGuardando(true);
    clienteAxios
      .post('/cuentas-vinculadas', {
        primary_user_id: principalId,
        secondary_user_id: secundariaId
      })
      .then(() => {
        message.success('Cuentas vinculadas.');
        setParEnRevision(null);
        setCandidatos((previos) =>
          previos.filter(
            (par) => !(par.account_a.user_id === a.user_id && par.account_b.user_id === b.user_id)
          )
        );
        cargarVinculos();
      })
      .catch((error) => {
        Swal.fire({
          icon: 'warning',
          title: 'No se pudo vincular',
          text: error.response?.data?.message || 'Intenta de nuevo.',
          confirmButtonText: 'ACEPTAR'
        });
      })
      .finally(() => setGuardando(false));
  };

  const descartarPar = (par) => {
    clienteAxios
      .post('/cuentas-vinculadas/descartar', {
        user_a_id: par.account_a.user_id,
        user_b_id: par.account_b.user_id
      })
      .then(() => {
        message.success('Par descartado. No volverá a proponerse.');
        setCandidatos((previos) => previos.filter((otro) => otro !== par));
      })
      .catch((error) => message.error(error.response?.data?.message || 'No se pudo descartar.'));
  };

  const desvincular = (vinculo) => {
    Swal.fire({
      icon: 'question',
      title: '¿Romper el vínculo?',
      html: `<b>${vinculo.primary_name}</b> dejará de poder moverse entre las dos empresas
             en una sola sesión. Las cuentas y sus datos no se tocan.`,
      showCancelButton: true,
      confirmButtonText: 'DESVINCULAR',
      cancelButtonText: 'CANCELAR'
    }).then((resultado) => {
      if (!resultado.isConfirmed) return;

      clienteAxios
        .delete(`/cuentas-vinculadas/${vinculo.id}`)
        .then(() => {
          message.success('Cuentas desvinculadas.');
          cargarVinculos();
        })
        .catch((error) =>
          message.error(error.response?.data?.message || 'No se pudo desvincular.')
        );
    });
  };

  const columnasCandidatos = [
    {
      title: 'CONFIANZA',
      dataIndex: 'confidence',
      key: 'confidence',
      render: (confianza, par) => (
        <Tag color={confianza === 'alta' ? 'green' : 'gold'}>
          {confianza} ({par.score})
        </Tag>
      )
    },
    {
      title: 'CUENTA A',
      key: 'a',
      render: (_, par) => <ResumenCuenta cuenta={par.account_a} />
    },
    {
      title: 'CUENTA B',
      key: 'b',
      render: (_, par) => <ResumenCuenta cuenta={par.account_b} />
    },
    {
      title: 'POR QUÉ SE PROPONE',
      key: 'signals',
      render: (_, par) => (
        <>
          {par.signals.map((senal) => (
            <Tag key={senal} color="blue">
              {senal}
            </Tag>
          ))}
          {par.warnings.map((aviso) => (
            <Tag key={aviso} color="red">
              {aviso}
            </Tag>
          ))}
        </>
      )
    },
    {
      title: 'ACCIONES',
      key: 'acciones',
      render: (_, par) => (
        <div className="flex gap-2">
          <Boton type="primary" onClick={() => revisarPar(par.account_a, par.account_b)}>
            REVISAR
          </Boton>
          <Boton onClick={() => descartarPar(par)}>NO SON LA MISMA</Boton>
        </div>
      )
    }
  ];

  const columnasVinculos = [
    {
      title: 'CUENTA PRINCIPAL',
      key: 'principal',
      render: (_, fila) => (
        <>
          <div>{fila.primary_name}</div>
          <small>{fila.primary_email}</small>
        </>
      )
    },
    {
      title: 'CUENTA SECUNDARIA',
      key: 'secundaria',
      render: (_, fila) => (
        <>
          <div>{fila.secondary_name}</div>
          <small>{fila.secondary_email}</small>
        </>
      )
    },
    { title: 'VINCULADA POR', dataIndex: 'linked_by_name', key: 'linked_by_name' },
    {
      title: 'DESDE',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (fecha) => (fecha ? String(fecha).substring(0, 10) : '')
    },
    {
      title: 'ACCIONES',
      key: 'acciones',
      render: (_, fila) => (
        <Boton danger icon={<DisconnectOutlined />} onClick={() => desvincular(fila)}>
          DESVINCULAR
        </Boton>
      )
    }
  ];

  const opcionesUsuarios = usuarios.map((usuario) => ({
    value: usuario.id,
    label: `${usuario.name} — ${usuario.email}${usuario.company ? ` (${usuario.company})` : ''}`
  }));

  return (
    <div className="mt-4">
      <Alert
        className="mb-4"
        type="info"
        showIcon
        message="Para quien trabaja en dos empresas del grupo"
        description="Vincular dos cuentas permite entrar con cualquiera de las dos y moverse entre
          empresas sin volver a iniciar sesión. Las cuentas NO se fusionan: cada una conserva su
          correo, su contraseña y su historial, y cada movimiento sigue quedando registrado en la
          empresa donde se hizo. Es reversible en cualquier momento."
      />

      <h3>Cuentas que podrían ser de la misma persona</h3>
      <p>
        La búsqueda solo <b>propone</b> pares comparando nombre, correo y empresa. Revísalos: puede
        haber familiares con los mismos apellidos, o correos de puesto que pasaron de una persona a
        otra.
      </p>
      <Boton type="primary" icon={<SearchOutlined />} loading={buscando} onClick={buscarCandidatos}>
        BUSCAR CANDIDATOS
      </Boton>

      {candidatos.length > 0 && (
        <Table
          className="mt-4 mb-4"
          rowKey={(par) => `${par.account_a.user_id}-${par.account_b.user_id}`}
          columns={columnasCandidatos}
          dataSource={candidatos}
          pagination={false}
          scroll={{ x: true }}
        />
      )}

      <h3 className="mt-4">Vincular dos cuentas concretas</h3>
      <p>Si ya sabes cuáles son, elígelas directamente sin pasar por la búsqueda.</p>
      <div className="flex gap-2 flex-wrap items-center">
        <Select
          showSearch
          placeholder="Cuenta A"
          style={{ minWidth: 320 }}
          optionFilterProp="label"
          value={manualA}
          onChange={setManualA}
          options={opcionesUsuarios}
        />
        <Select
          showSearch
          placeholder="Cuenta B"
          style={{ minWidth: 320 }}
          optionFilterProp="label"
          value={manualB}
          onChange={setManualB}
          options={opcionesUsuarios}
        />
        <Boton type="primary" icon={<LinkOutlined />} onClick={revisarParManual}>
          COMPARAR
        </Boton>
      </div>

      <h3 className="mt-4">Vínculos activos</h3>
      {vinculos.length === 0 && !cargando ? (
        <Empty description="Todavía no hay cuentas vinculadas" />
      ) : (
        <Table
          className="mt-2"
          rowKey="id"
          columns={columnasVinculos}
          dataSource={vinculos}
          loading={cargando}
          pagination={false}
          scroll={{ x: true }}
        />
      )}

      <Modal
        title="Confirmar que son la misma persona"
        open={!!parEnRevision}
        onCancel={() => setParEnRevision(null)}
        onOk={vincular}
        okText="VINCULAR"
        cancelText="CANCELAR"
        confirmLoading={guardando}
        width={720}>
        {parEnRevision && (
          <>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Cuenta A">
                <ResumenCuenta cuenta={parEnRevision.a} />
              </Descriptions.Item>
              <Descriptions.Item label="Cuenta B">
                <ResumenCuenta cuenta={parEnRevision.b} />
              </Descriptions.Item>
            </Descriptions>

            <p className="mt-4">
              <b>¿Cuál es la cuenta principal?</b> Es la identidad de referencia de la persona. Las
              dos siguen sirviendo para entrar.
            </p>
            <Radio.Group value={principalId} onChange={(e) => setPrincipalId(e.target.value)}>
              <Radio value={parEnRevision.a.user_id}>{parEnRevision.a.email}</Radio>
              <Radio value={parEnRevision.b.user_id}>{parEnRevision.b.email}</Radio>
            </Radio.Group>

            <Alert
              className="mt-4"
              type="warning"
              showIcon
              message="Esto concede acceso cruzado"
              description="Quien tenga la contraseña de una de las dos cuentas podrá operar como la
                otra. Vincula solo si estás seguro de que son la misma persona, y rompe el vínculo
                si deja de trabajar en alguna de las dos empresas."
            />
          </>
        )}
      </Modal>
    </div>
  );
};

const ResumenCuenta = ({ cuenta }) => (
  <div>
    <div>{cuenta.name}</div>
    <small>{cuenta.email}</small>
    <div>
      {cuenta.company ? <Tag>{cuenta.company}</Tag> : <Tag color="red">sin empresa</Tag>}
      {cuenta.intranet_id ? <Tag color="purple">intranet #{cuenta.intranet_id}</Tag> : null}
    </div>
  </div>
);

ResumenCuenta.propTypes = {
  cuenta: PropTypes.shape({
    user_id: PropTypes.number,
    name: PropTypes.string,
    email: PropTypes.string,
    company: PropTypes.string,
    intranet_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired
};

export default CuentasVinculadas;
